/* ==========================================================================
   EMMA GLOVER DESIGN — redesign behaviour
   Small, dependency-free companion to assets/redesign.css.
   Only adds scroll-reveal. Everything else is CSS.
   ========================================================================== */
(function () {
  'use strict';

  var REVEAL_TARGETS = [
    '.Section .SectionHeader',
    '.ProductList > .Grid__Cell',
    '.CollectionList > *',
    '.Doors > .Door',
    '.ArticleList > *',
    '.TestimonialList',
    '.ImageHero__Wrapper'
  ].join(',');

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init() {
    if (reduced || !('IntersectionObserver' in window)) return;

    var nodes = document.querySelectorAll(REVEAL_TARGETS);
    if (!nodes.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('eg-in');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px' });

    Array.prototype.forEach.call(nodes, function (node, i) {
      // Anything already in view on load reveals immediately - no flash of hidden content.
      if (node.getBoundingClientRect().top < window.innerHeight) {
        node.classList.add('eg-rv', 'eg-in');
        return;
      }
      node.classList.add('eg-rv');
      node.style.transitionDelay = (Math.min(i % 4, 3) * 70) + 'ms';
      io.observe(node);
    });
  }

  /* ------------------------------------------------------------------------
     Collection tile titles: shrink a title that would wrap so it holds one
     line. "Gold Pendants & Bracelets" is the case this exists for.
     ------------------------------------------------------------------------ */

  var MIN_SCALE = 0.62;   // never shrink past this much of the responsive size
  var TILE_HEADING = '.CollectionItem .SectionHeader__Heading';

  function fitTileHeadings() {
    var nodes = Array.prototype.slice.call(document.querySelectorAll(TILE_HEADING));
    if (!nodes.length) return;

    // ---- pass 1: reset, then work out what each title needs to hold one line
    var measured = nodes.map(function (el) {
      el.style.removeProperty('font-size');
      el.style.removeProperty('white-space');

      var base = parseFloat(window.getComputedStyle(el).fontSize) || 0;
      el.style.setProperty('white-space', 'nowrap');

      var available = el.clientWidth;
      var needed = el.scrollWidth;
      // Text width scales roughly linearly with font size, so the ratio gives
      // the answer in one step instead of looping a pixel at a time.
      var scale = (available && needed > available) ? (available / needed) * 0.98 : 1;

      return {
        el: el,
        base: base,
        scale: scale,
        // group by visual row — tiles that sit side by side share a top edge
        row: Math.round(el.getBoundingClientRect().top / 8)
      };
    });

    // ---- pass 2: one size per row, so a long title doesn't leave its
    // neighbours looking like a different design
    var rows = {};
    measured.forEach(function (m) {
      if (!rows[m.row] || m.scale < rows[m.row]) rows[m.row] = m.scale;
    });

    measured.forEach(function (m) {
      var scale = Math.max(rows[m.row], MIN_SCALE);
      if (scale >= 1) {
        m.el.style.removeProperty('white-space');
        return;
      }
      setSize(m.el, m.base * scale);

      // kerning and hinting make the ratio imperfect; nudge a few times
      var size = m.base * scale;
      for (var i = 0; i < 6 && m.el.scrollWidth > m.el.clientWidth + 1; i++) {
        size -= 0.5;
        if (size < m.base * MIN_SCALE) break;
        setSize(m.el, size);
      }

      // Still too long even at the floor — let this one wrap rather than
      // clip, but leave the rest of the row alone.
      if (m.el.scrollWidth > m.el.clientWidth + 1) {
        m.el.style.removeProperty('white-space');
      }
    });
  }

  function setSize(el, px) {
    // !important because the responsive rule this overrides is itself
    // !important — see the note in redesign.css.
    el.style.setProperty('font-size', px + 'px', 'important');
  }

  /* ------------------------------------------------------------------------
     Homepage header state. Prestige ships no scrolled class, so we add one.
     Only ever toggles a class — the header's box height is deliberately
     identical in both states (see the note in redesign.css), so this can never
     invalidate the --header-height the theme writes on load.
     ------------------------------------------------------------------------ */

  var SCROLL_TRIGGER = 60;
  var scrollTicking = false;

  function applyHeaderState() {
    scrollTicking = false;
    var y = window.pageYOffset || document.documentElement.scrollTop || 0;
    document.documentElement.classList.toggle('eg-scrolled', y > SCROLL_TRIGGER);
  }

  function onScroll() {
    if (scrollTicking) return;
    scrollTicking = true;
    window.requestAnimationFrame(applyHeaderState);
  }

  /* ------------------------------------------------------------------------
     Wishlist button state. The iWish app swaps the link's text between
     iwish_add_txt and iwish_added_txt but adds no class, so there is nothing
     for CSS to hook. Watch the text and mirror it onto a class, which lets the
     heart fill in. Both strings are globals the app itself defines.
     ------------------------------------------------------------------------ */

  function syncWishlistState(el) {
    var added = window.iwish_added_txt || 'Added in Wishlist';
    var addedCol = window.iwish_added_txt_col || added;
    var text = (el.textContent || '').trim();
    el.classList.toggle('eg-wishlisted', text === added || text === addedCol);
  }

  function watchWishlist() {
    var nodes = document.querySelectorAll('.iWishAdd, .iWishAddColl');
    if (!nodes.length || !window.MutationObserver) return;

    Array.prototype.forEach.call(nodes, function (el) {
      if (el.getAttribute('data-eg-watched')) return;
      el.setAttribute('data-eg-watched', '1');
      syncWishlistState(el);
      new MutationObserver(function () { syncWishlistState(el); })
        .observe(el, { childList: true, characterData: true, subtree: true });
    });
  }

  /* ------------------------------------------------------------------------
     Consultation form: the piece-type tiles drive which fields appear.

     Fields start VISIBLE in the markup and are hidden here, so if this script
     never runs the form degrades to showing everything rather than hiding
     fields the visitor can never reach.

     Hidden fields are also disabled — otherwise a ring size entered and then
     switched away from would still be submitted.
     ------------------------------------------------------------------------ */

  function initConsultForm() {
    var form = document.querySelector('.ConsultForm__Form');
    if (!form) return;

    var radios = form.querySelectorAll('[data-piece]');
    var conditionals = form.querySelectorAll('.ConsultForm__Conditional');
    if (!radios.length || !conditionals.length) return;

    function apply() {
      var checked = form.querySelector('[data-piece]:checked');
      var piece = checked ? checked.getAttribute('data-piece') : null;
      var needsRingSize = checked ? checked.hasAttribute('data-needs-ring-size') : false;

      Array.prototype.forEach.call(conditionals, function (block) {
        var when = block.getAttribute('data-when');
        var show = (when === 'other' && piece === 'other') ||
                   (when === 'ring-size' && needsRingSize);

        block.hidden = !show;
        Array.prototype.forEach.call(
          block.querySelectorAll('input, select, textarea'),
          function (field) { field.disabled = !show; }
        );
      });
    }

    Array.prototype.forEach.call(radios, function (radio) {
      radio.addEventListener('change', apply);
    });

    apply();
  }


  /* ------------------------------------------------------------------------
     DROP A HINT

     One dialog serves the whole site (sections/drop-a-hint.liquid, rendered at
     body level). This code does four things and deliberately not a fifth:

       1. Reveals the triggers, but only if the dialog is actually present and
          configured. The triggers ship with `hidden` for exactly this reason —
          a trigger that opens nothing is worse than no trigger.
       2. Paints the dialog's summary from the clicked trigger's data-hint-*
          attributes, so one dialog can describe any product.
       3. On the wishlist page, builds a trigger from what is rendered there.
       4. Submits, and reports back inside the dialog.

     It does NOT open or close the dialog. theme.js owns that, via its
     delegated [data-action="open-modal"] listener.

     The click listener is registered in the CAPTURE phase on purpose:
     theme.js binds to document.body and calls stopPropagation(), so a normal
     bubble-phase listener on document would never run. Capture fires first,
     which also means the summary is painted before the dialog becomes
     visible — no flash of the previous product.
     ------------------------------------------------------------------------ */

  var HINT_MIN_ITEMS_FOR_STRIP = 2;

  function hintModal() {
    return document.querySelector('[data-drop-hint]');
  }

  /* Shopify's image filters and lazysizes both hand back protocol-relative
     urls (//cdn.shopify.com/...). The API validates that every image is https
     before it will render it in an email, and a scheme-less url does not
     satisfy that — so images would silently vanish from the hint. */
  function hintAbsoluteUrl(url) {
    if (!url) return '';
    if (url.indexOf('//') === 0) return window.location.protocol + url;
    if (url.indexOf('/') === 0) return window.location.origin + url;
    return url;
  }

  function hintEscape(value) {
    var div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  /* Product links on the current page, deduped by path.

     Used for the wishlist, whose markup belongs to the iWish app and is served
     through Shopify's app proxy — we cannot see it from the theme and it can
     change without notice. Rather than depend on their classes, read the one
     thing any rendering of a wishlist must contain: links to /products/. That
     survives a redesign on their side. */
  function hintCollectProductLinks(root) {
    var links = (root || document).querySelectorAll('a[href*="/products/"]');
    var seen = {};
    var items = [];

    Array.prototype.forEach.call(links, function (link) {
      var path = (link.pathname || '').split('?')[0];
      if (!path || path.indexOf('/products/') === -1) return;
      if (seen[path]) return;
      seen[path] = true;

      // The image is usually inside the link; on a table-style layout it is in
      // a sibling cell, so fall back to the closest shared ancestor.
      var img = link.querySelector('img');
      if (!img) {
        var row = link.closest('tr, li, .Grid__Cell, div');
        if (row) img = row.querySelector('img');
      }

      var title = (link.getAttribute('title') || link.textContent || '').replace(/\s+/g, ' ').trim();
      if (!title && img) title = img.getAttribute('alt') || '';

      items.push({
        title: title.slice(0, 200),
        url: link.protocol + '//' + link.host + path,
        image: hintAbsoluteUrl(img ? (img.getAttribute('data-src') || img.currentSrc || img.src || '') : '')
      });
    });

    return items;
  }

  function hintPaintSummary(modal, items) {
    var summary = modal.querySelector('[data-drop-hint-summary]');
    if (!summary) return;

    if (!items.length) {
      summary.innerHTML = '';
      return;
    }

    if (items.length < HINT_MIN_ITEMS_FOR_STRIP) {
      var item = items[0];
      summary.innerHTML =
        (item.image ? '<img class="DropHint__SummaryImage" src="' + hintEscape(item.image) + '" alt="">' : '') +
        '<div class="DropHint__SummaryText">' +
          '<p class="DropHint__SummaryTitle">' + hintEscape(item.title) + '</p>' +
          (item.price ? '<p class="DropHint__SummaryMeta">' + hintEscape(item.price) + '</p>' : '') +
        '</div>';
      return;
    }

    var strip = items.slice(0, 6).map(function (entry) {
      return entry.image ? '<img src="' + hintEscape(entry.image) + '" alt="">' : '';
    }).join('');

    summary.innerHTML =
      '<div class="DropHint__SummaryText">' +
        '<p class="DropHint__SummaryTitle">' + items.length + ' pieces</p>' +
        '<div class="DropHint__SummaryStrip">' + strip + '</div>' +
      '</div>';
  }

  function hintReset(modal) {
    var form = modal.querySelector('[data-drop-hint-form]');
    var success = modal.querySelector('[data-drop-hint-success]');
    var error = modal.querySelector('[data-drop-hint-error]');

    if (form) form.hidden = false;
    if (success) success.hidden = true;
    if (error) { error.hidden = true; error.textContent = ''; }

    // Back to "I don't know" — a size left over from the last hint must not
    // carry into the next one.
    var size = modal.querySelector('[data-drop-hint-size] select');
    if (size) size.value = '';
  }

  function hintPrepare(modal, trigger) {
    var items;
    var kind = trigger.getAttribute('data-hint-kind') || 'product';

    if (kind === 'wishlist') {
      items = modal.hintWishlistItems || [];
    } else {
      items = [ {
        title: trigger.getAttribute('data-hint-title') || '',
        url: hintAbsoluteUrl(trigger.getAttribute('data-hint-url') || ''),
        image: hintAbsoluteUrl(trigger.getAttribute('data-hint-image') || ''),
        price: trigger.getAttribute('data-hint-price') || '',
        variant_id: trigger.getAttribute('data-hint-variant') || ''
      } ];
    }

    modal.hintItems = items;
    modal.hintKind = kind;

    /* Ring size. Disabled as well as hidden: hiding alone would still submit
       whatever the select holds, so a size chosen on a ring could ride along
       on the next hint about a pendant. Only single-product hints ask — a
       wishlist can hold several pieces and one size would not describe them. */
    var sizeBlock = modal.querySelector('[data-drop-hint-size]');
    if (sizeBlock) {
      var wantsSize = kind !== 'wishlist' && trigger.getAttribute('data-hint-sized') === 'true';
      var sizeField = sizeBlock.querySelector('select');
      sizeBlock.hidden = !wantsSize;
      if (sizeField) sizeField.disabled = !wantsSize;
    }
    // Stamped here rather than on page load: the server rejects anything
    // submitted within three seconds of the dialog opening, and a visitor who
    // left the page open for an hour should not be treated as suspicious.
    modal.hintRenderedAt = Date.now();

    hintReset(modal);
    hintPaintSummary(modal, items);
  }

  function hintSubmit(modal) {
    var form = modal.querySelector('[data-drop-hint-form]');
    var button = modal.querySelector('[data-drop-hint-submit]');
    var label = modal.querySelector('[data-drop-hint-submit-label]');
    var error = modal.querySelector('[data-drop-hint-error]');
    var success = modal.querySelector('[data-drop-hint-success]');
    var endpoint = modal.getAttribute('data-endpoint');
    var key = modal.getAttribute('data-public-key');

    function fail(message) {
      if (!error) return;
      error.textContent = message;
      error.hidden = false;
    }

    if (error) { error.hidden = true; error.textContent = ''; }

    var data = new FormData(form);
    var recipient = (data.get('recipient_email') || '').trim();
    var sender = (data.get('sender_email') || '').trim();

    // Cheap client-side checks only. The server repeats all of them; these
    // exist so the common mistakes do not cost a round trip.
    if (!recipient || recipient.indexOf('@') === -1) return fail('Please enter the email address of the person you are hinting to.');
    if (!sender || sender.indexOf('@') === -1) return fail('Please enter your own email address.');
    if (recipient.toLowerCase() === sender.toLowerCase()) return fail('That is your own address — try the address of the person you are hinting to.');
    if (!modal.hintItems || !modal.hintItems.length) return fail('There is nothing to hint about yet.');

    var original = label ? label.textContent : '';
    if (button) button.disabled = true;
    if (label) label.textContent = 'Sending...';

    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Public-Key': key },
      body: JSON.stringify({
        kind: modal.hintKind || 'product',
        recipient_email: recipient,
        sender_email: sender,
        sender_name: (data.get('sender_name') || '').trim(),
        note: (data.get('note') || '').trim(),
        items: modal.hintItems,
        // "I don't know" is the empty value, so the key is left out entirely
        // rather than sent blank — the email has no size line to render.
        ring_size: (data.get('ring_size') || '').trim() || undefined,
        copy_to_sender: data.get('copy_to_sender') ? true : false,
        subscribe: data.get('subscribe') ? true : false,
        website: data.get('website') || '',
        rendered_at: modal.hintRenderedAt,
        source_url: window.location.href
      })
    }).then(function (response) {
      return response.json().catch(function () { return {}; }).then(function (body) {
        return { ok: response.ok, body: body };
      });
    }).then(function (result) {
      if (button) button.disabled = false;
      if (label) label.textContent = original;

      if (!result.ok) {
        var message = result.body && result.body.error;
        if (Object.prototype.toString.call(message) === '[object Array]') message = message.join(' ');
        return fail(message || 'Something went wrong. Please try again in a moment.');
      }

      if (form) form.hidden = true;
      if (success) success.hidden = false;
    }).catch(function () {
      if (button) button.disabled = false;
      if (label) label.textContent = original;
      fail('We could not reach the server. Please check your connection and try again.');
    });
  }

  /* The wishlist page is rendered by the iWish app through Shopify's app
     proxy, so there is no template to edit — the bar is inserted above
     whatever they rendered. */
  function initWishlistHint(modal) {
    if (window.location.pathname.indexOf('/apps/iwish') !== 0) return;

    var main = document.getElementById('main') || document.body;
    if (main.querySelector('[data-drop-hint-wishlist-bar]')) return;

    var items = hintCollectProductLinks(main);
    if (!items.length) return;

    modal.hintWishlistItems = items;

    var bar = document.createElement('div');
    bar.className = 'DropHint__WishlistBar';
    bar.setAttribute('data-drop-hint-wishlist-bar', '1');

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'DropHint__Trigger';
    button.setAttribute('data-action', 'open-modal');
    button.setAttribute('aria-controls', 'modal-drop-a-hint');
    button.setAttribute('data-drop-hint-trigger', '1');
    button.setAttribute('data-hint-kind', 'wishlist');
    button.textContent = modal.getAttribute('data-wishlist-trigger-label') || 'Drop a hint about this wishlist';

    bar.appendChild(button);

    var anchor = main.querySelector('.Container') || main.firstElementChild || main;
    anchor.insertBefore(bar, anchor.firstChild);
  }

  function initDropHint() {
    var modal = hintModal();
    if (!modal) return;

    // Reveal the product-page triggers now that we know the dialog exists.
    var triggers = document.querySelectorAll('[data-drop-hint-trigger][hidden]');
    var triggerLabel = modal.getAttribute('data-trigger-label');
    Array.prototype.forEach.call(triggers, function (trigger) {
      if (triggerLabel) trigger.textContent = triggerLabel;
      trigger.hidden = false;
    });

    initWishlistHint(modal);

    if (modal.getAttribute('data-eg-hint-bound')) return;
    modal.setAttribute('data-eg-hint-bound', '1');

    document.addEventListener('click', function (event) {
      var trigger = event.target.closest && event.target.closest('[data-drop-hint-trigger]');
      if (trigger) hintPrepare(modal, trigger);
    }, true);

    var noteToggle = modal.querySelector('[data-drop-hint-note-toggle]');
    var note = modal.querySelector('#DropHintNote');
    if (noteToggle && note) {
      noteToggle.addEventListener('click', function () {
        var open = note.hidden;
        note.hidden = !open;
        noteToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) note.focus();
      });
    }

    var form = modal.querySelector('[data-drop-hint-form]');
    if (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        hintSubmit(modal);
      });
    }

    var again = modal.querySelector('[data-drop-hint-reset]');
    if (again) {
      again.addEventListener('click', function () {
        hintReset(modal);
        modal.hintRenderedAt = Date.now();
        var first = modal.querySelector('#DropHintRecipient');
        if (first) { first.value = ''; first.focus(); }
      });
    }
  }

  var resizeTimer;
  function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(fitTileHeadings, 150);
  }

  function initFit() {
    fitTileHeadings();
    // Re-measure once webfonts land; fallback metrics give the wrong answer.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(fitTileHeadings);
    }
  }

  function boot() {
    init();
    initFit();
    applyHeaderState();
    watchWishlist();
    initConsultForm();
    initDropHint();
    // the app script loads on window.load, after DOMContentLoaded
    window.addEventListener('load', watchWishlist);
    // The wishlist page is painted by the iWish app on window.load, so the
    // product rows do not exist yet at DOMContentLoaded.
    window.addEventListener('load', initDropHint);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.addEventListener('resize', onResize);
  window.addEventListener('scroll', onScroll, { passive: true });

  // Shopify theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', boot);
})();
